const AUTH_KEYS = [
  "accessLevel",
  "bearer",
  "companies_object",
  "company_id",
  "lastRequestTime",
  "refreshToken",
  "token",
];

const PENDING_REMEMBER_KEY = "pendingRememberMe";
const AUTH_CHANNEL_NAME = "koncurent-auth-session";
const AUTH_SESSION_REQUEST = "auth-session-request";
const AUTH_SESSION_RESPONSE = "auth-session-response";
const SESSION_SYNC_KEYS = [...AUTH_KEYS, "email"];

const getStorage = (remember) =>
  remember ? window.localStorage : window.sessionStorage;

const getSessionAuthData = () =>
  SESSION_SYNC_KEYS.reduce((authData, key) => {
    const value = window.sessionStorage.getItem(key);

    if (value !== null) {
      authData[key] = value;
    }

    return authData;
  }, {});

const createRequestId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random()}`;
};

const authChannel =
  typeof window !== "undefined" && "BroadcastChannel" in window
    ? new window.BroadcastChannel(AUTH_CHANNEL_NAME)
    : null;

const removeAuthKeys = (storage) => {
  AUTH_KEYS.forEach((key) => storage.removeItem(key));
};

const clearStorageExcept = (storage, keysToKeep) => {
  const savedValues = keysToKeep.reduce((values, key) => {
    const value = storage.getItem(key);

    if (value !== null) {
      values[key] = value;
    }

    return values;
  }, {});

  storage.clear();
  Object.entries(savedValues).forEach(([key, value]) => storage.setItem(key, value));
};

if (authChannel) {
  authChannel.addEventListener("message", ({ data }) => {
    if (
      data?.type !== AUTH_SESSION_REQUEST ||
      !data.requestId ||
      !window.sessionStorage.getItem("token")
    ) {
      return;
    }

    authChannel.postMessage({
      type: AUTH_SESSION_RESPONSE,
      requestId: data.requestId,
      authData: getSessionAuthData(),
    });
  });
}

export const AuthStorage = {
  getItem(key) {
    return getStorage(this.shouldRemember()).getItem(key);
  },

  setItem(key, value) {
    getStorage(this.shouldRemember()).setItem(key, value);
  },

  removeItem(key) {
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  },

  shouldRemember() {
    return window.localStorage.getItem("remember") === "true";
  },

  restoreSessionFromOpenTab(timeout = 800) {
    if (this.getItem("token")) {
      return Promise.resolve(true);
    }

    if (this.shouldRemember() || !authChannel) {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const requestId = createRequestId();
      let timeoutId;

      const finish = (restored) => {
        window.clearTimeout(timeoutId);
        authChannel.removeEventListener("message", handleResponse);
        resolve(restored);
      };

      const handleResponse = ({ data }) => {
        if (
          data?.type !== AUTH_SESSION_RESPONSE ||
          data.requestId !== requestId ||
          !data.authData?.token
        ) {
          return;
        }

        Object.entries(data.authData).forEach(([key, value]) => {
          if (SESSION_SYNC_KEYS.includes(key) && value !== null) {
            window.sessionStorage.setItem(key, value);
          }
        });
        finish(true);
      };

      authChannel.addEventListener("message", handleResponse);
      timeoutId = window.setTimeout(() => finish(false), timeout);
      authChannel.postMessage({ type: AUTH_SESSION_REQUEST, requestId });
    });
  },

  prepareLogin(remember) {
    this.clearAuthData();
    window.sessionStorage.setItem(PENDING_REMEMBER_KEY, String(remember));

    if (remember) {
      window.localStorage.setItem("remember", "true");
    } else {
      window.localStorage.removeItem("remember");
      window.localStorage.removeItem("email");
    }
  },

  completeLogin(data) {
    const pendingRemember = window.sessionStorage.getItem(PENDING_REMEMBER_KEY);
    const remember =
      pendingRemember === null ? this.shouldRemember() : pendingRemember === "true";
    const storage = getStorage(remember);

    this.clearAuthData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        storage.setItem(key, value);
      }
    });

    if (remember) {
      window.localStorage.setItem("remember", "true");
    } else {
      window.localStorage.removeItem("remember");
    }

    window.sessionStorage.removeItem(PENDING_REMEMBER_KEY);
  },

  clearAuthData() {
    removeAuthKeys(window.localStorage);
    removeAuthKeys(window.sessionStorage);
    window.localStorage.removeItem("url");
  },

  clearAll() {
    clearStorageExcept(window.localStorage, ["email", "remember"]);
    clearStorageExcept(window.sessionStorage, ["email"]);
  },
};
